import React, { useContext } from "react";
import PropTypes from "prop-types";
import Chart from "react-apexcharts";
import { ThemeContext } from "../layout/themeContext";
import { compact, money, weekdayOf } from "./incomeLabels";

// The income of each day as a column: a single series, so the card's title names it
// and there is no legend. The daily target is a line across the same axis, never an
// axis of its own. Colors are those of the tenant's charts (TenantTrendChart.js).
const THEMES = {
  light: { series: "#7C3AED", grid: "#E5E7EB", text: "#6B7280" },
  dark: { series: "#8B5CF6", grid: "#374151", text: "#9CA3AF" },
};

// An axis from 0 that holds top, in steps of a round number: 237,300 is 0 to 250,000
// by 50,000
const axisOf = (top) => {
  const rough = top / 5;
  const power = 10 ** Math.floor(Math.log10(rough));
  const step = [1, 2, 2.5, 5, 10].find((size) => size * power >= rough) * power;
  const tickAmount = Math.ceil(top / step);
  return { max: step * tickAmount, tickAmount };
};

// Columns stay thin however few the days: about 24px on a plot so many px wide
const columnWidthOf = (count, plot) => `${Math.min(60, Math.max((count * 2400) / plot, 3))}%`;

// Every so many days are named on the axis, so the names do not run into each other
const labelOf = (days, most) => {
  const every = Math.ceil(days.length / most);
  return (value) => (days.indexOf(value) % every === 0 ? String(value).slice(5) : "");
};

const DailyIncomeChart = ({ days, values, target, name, height }) => {
  const { theme } = useContext(ThemeContext);
  const mode = theme === "dark" ? "dark" : "light";
  const colors = THEMES[mode];
  // The target's line stays on the plot when no day reached it
  const top = Math.max(target, ...values);

  const options = {
    chart: {
      type: "bar",
      background: "transparent",
      fontFamily: "Inter, sans-serif",
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    theme: { mode },
    colors: [colors.series],
    plotOptions: {
      bar: {
        columnWidth: columnWidthOf(days.length, 1000),
        borderRadius: 4,
        borderRadiusApplication: "end",
      },
    },
    states: { active: { filter: { type: "none" } } },
    dataLabels: { enabled: false },
    legend: { show: false },
    grid: {
      borderColor: colors.grid,
      strokeDashArray: 0,
      xaxis: { lines: { show: false } },
      // The target's name stands beside the plot, clear of the columns
      padding: { left: 8, right: target > 0 ? 76 : 8 },
    },
    xaxis: {
      categories: days,
      labels: {
        rotate: 0,
        hideOverlappingLabels: false,
        formatter: labelOf(days, 16),
        style: { colors: colors.text },
      },
      axisBorder: { color: colors.grid },
      axisTicks: { show: false },
      tooltip: { enabled: false },
    },
    yaxis: {
      min: 0,
      ...(top > 0 ? axisOf(top) : {}),
      labels: { formatter: compact, style: { colors: colors.text } },
    },
    annotations: {
      yaxis:
        target > 0
          ? [
              {
                y: target,
                borderColor: colors.text,
                strokeDashArray: 4,
                label: {
                  text: `Зорилт ${compact(target)}`,
                  position: "right",
                  textAnchor: "start",
                  offsetX: 8,
                  offsetY: 7,
                  borderWidth: 0,
                  style: { background: "transparent", color: colors.text, fontSize: "11px" },
                },
              },
            ]
          : [],
    },
    tooltip: {
      theme: mode,
      x: {
        formatter: (value, { dataPointIndex }) => {
          const day = days[dataPointIndex];
          return day ? `${day} · ${weekdayOf(day)}` : value;
        },
      },
      y: { formatter: (value) => money(value) },
    },
    // A phone's plot is narrow: fewer days on its axis, and wider shares for its columns
    responsive: [
      {
        breakpoint: 640,
        options: {
          plotOptions: { bar: { columnWidth: columnWidthOf(days.length, 250) } },
          xaxis: { labels: { formatter: labelOf(days, 5) } },
        },
      },
    ],
  };

  return <Chart options={options} series={[{ name, data: values }]} type="bar" height={height} />;
};

DailyIncomeChart.propTypes = {
  days: PropTypes.array.isRequired,
  values: PropTypes.array.isRequired,
  target: PropTypes.number,
  name: PropTypes.string,
  height: PropTypes.number,
};
DailyIncomeChart.defaultProps = { target: 0, name: "Орлого", height: 280 };

export default DailyIncomeChart;
